import { useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { MarketingHeader, MarketingFooter, MButton, BookCTA } from "@/components/marketing";
import { SOLUTIONS_BY_SLUG, BOOK_HREF, CONTACT_HREF } from "@/data/solutions";

const TEAL = "#22D3C5";
const EASE = [0.22, 1, 0.36, 1];

function PulseDot({ size = 7 }) {
  return (
    <span className="relative inline-flex" style={{ width: size, height: size }} aria-hidden="true">
      <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: TEAL, opacity: 0.4 }} />
      <span className="relative rounded-full" style={{ width: size, height: size, backgroundColor: TEAL }} />
    </span>
  );
}

// Adaptive product-style visualisation built from the solution's own benefits.
function SolutionVisual({ s }) {
  const rows = (s.benefits || []).slice(0, 4);
  return (
    <div
      aria-hidden="true"
      className="relative rounded-2xl border border-border bg-card p-5 sm:p-6 select-none"
      style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.04), 0 20px 50px -30px rgba(17,17,17,0.2)" }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center">
            <s.Icon className="h-4 w-4 text-foreground" strokeWidth={1.75} />
          </div>
          <span className="text-[13px] font-medium text-foreground">{s.navTitle}</span>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-border px-3 py-1">
          <PulseDot size={6} />
          <span className="text-[11px] tracking-wide text-muted-foreground">Aktiv</span>
        </div>
      </div>
      <div className="my-5 h-px bg-border" />
      <p className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground/70 mb-3">Leverer</p>
      <div className="space-y-2.5">
        {rows.map((b) => (
          <div key={b.title} className="flex items-center gap-3 rounded-xl border border-border bg-secondary/60 px-3.5 py-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-card border border-border shrink-0">
              <b.Icon className="h-3.5 w-3.5" style={{ color: TEAL }} strokeWidth={1.75} />
            </span>
            <span className="text-[13px] text-foreground truncate">{b.title}</span>
            <span className="ml-auto h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: TEAL }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// Browser / wireframe visual for the "hjemmesider" solution
function BrowserWireframe() {
  return (
    <div aria-hidden className="rounded-2xl border border-border bg-card overflow-hidden select-none"
      style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.04), 0 20px 50px -30px rgba(17,17,17,0.2)" }}>
      <div className="flex items-center gap-2 border-b border-border px-4 py-3 bg-secondary/60">
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <div className="ml-3 flex-1 rounded-md border border-border bg-card px-3 py-1 text-[11px] text-muted-foreground">jeresvirksomhed.dk</div>
      </div>
      <div className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="h-2 w-16 rounded bg-border" />
          <div className="flex gap-2">
            <div className="h-2 w-8 rounded bg-border" />
            <div className="h-2 w-8 rounded bg-border" />
            <div className="h-6 w-16 rounded-md" style={{ backgroundColor: TEAL }} />
          </div>
        </div>
        <div className="rounded-xl border border-border bg-secondary/50 p-4">
          <div className="h-3 w-2/3 rounded bg-foreground/80" />
          <div className="h-2 w-1/2 rounded bg-border mt-2.5" />
          <div className="h-7 w-24 rounded-md mt-4" style={{ backgroundColor: TEAL }} />
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-lg border border-border bg-card p-3">
              <div className="h-6 w-6 rounded-md bg-secondary mb-2" />
              <div className="h-1.5 w-full rounded bg-border" />
              <div className="h-1.5 w-2/3 rounded bg-border mt-1.5" />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/50 px-3.5 py-2.5">
          <span className="text-[11px] text-muted-foreground">Konverteringsflow</span>
          <span className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color: TEAL }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: TEAL }} /> Optimeret
          </span>
        </div>
      </div>
    </div>
  );
}

// System flow visual for the "ai-agenter" solution
function AgentFlow() {
  const steps = [
    { label: "Opgave modtaget", note: "Input" },
    { label: "Finder relevant viden", note: "Vidensbase" },
    { label: "Udfører handling", note: "Afgrænset" },
    { label: "Resultat kontrolleres", note: "Kvalitet" },
    { label: "Afsluttet / sendt videre", note: "Klar" },
  ];
  return (
    <div aria-hidden className="rounded-2xl border border-border bg-card p-5 sm:p-6 select-none"
      style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.04), 0 20px 50px -30px rgba(17,17,17,0.2)" }}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <span className="relative inline-flex h-2 w-2">
            <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: TEAL, opacity: 0.4 }} />
            <span className="relative h-2 w-2 rounded-full" style={{ backgroundColor: TEAL }} />
          </span>
          <span className="text-[13px] font-medium text-foreground">AI-agent</span>
        </div>
        <span className="rounded-full border border-border px-3 py-1 text-[11px] tracking-wide text-muted-foreground">Arbejder</span>
      </div>
      <div className="relative pl-6">
        <div className="absolute left-[9px] top-2 bottom-2 w-px bg-border" />
        <div className="space-y-3">
          {steps.map((s, i) => {
            const last = i === steps.length - 1;
            return (
              <div key={s.label} className="relative flex items-center gap-3 rounded-xl border border-border bg-secondary/60 px-3.5 py-2.5">
                <span className="absolute -left-6 flex h-[18px] w-[18px] items-center justify-center rounded-full border bg-card"
                  style={{ borderColor: last ? TEAL : "#DDD8D0", backgroundColor: last ? TEAL : "#FFFFFF" }}>
                  {last ? <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} /> : <span className="text-[9px] text-muted-foreground">{i + 1}</span>}
                </span>
                <span className="text-[13px] text-foreground">{s.label}</span>
                <span className="ml-auto text-[11px] text-muted-foreground">{s.note}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function HeroVisual({ s, slug }) {
  if (slug === "hjemmesider") return <BrowserWireframe />;
  if (slug === "ai-agenter") return <AgentFlow />;
  return <SolutionVisual s={s} />;
}

export default function Solution() {
  const { slug } = useParams();
  const s = SOLUTIONS_BY_SLUG[slug];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!s) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="grain" />
      <MarketingHeader />

      {/* Hero — editorial, asymmetric */}
      <section className="relative z-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 pt-16 sm:pt-20 pb-16 sm:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
            <div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 mb-6">
                <PulseDot size={6} />
                <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{s.navTitle}</span>
              </motion.div>
              <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08, ease: EASE }}
                className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05] text-balance">
                {s.heroTitle}{s.heroTitleAccent && <span style={{ color: TEAL }}>{s.heroTitleAccent}</span>}
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.16, ease: EASE }}
                className="text-muted-foreground mt-6 text-base sm:text-lg leading-relaxed max-w-xl">{s.heroLead}</motion.p>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.24, ease: EASE }}
                className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <MButton href={BOOK_HREF} size="lg" data-testid="hero-book">Book et møde <ArrowRight className="h-4 w-4" /></MButton>
                <MButton href={CONTACT_HREF} size="lg" variant="secondary" data-testid="hero-contact">Kontakt os</MButton>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3, ease: EASE }}>
              <HeroVisual s={s} slug={slug} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hvad er det? — editorial text */}
      <section className="relative z-10 border-y border-border bg-secondary/50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-[0.4fr_0.6fr] gap-8 lg:gap-16">
          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground pt-1">Hvad er det?</p>
          <div className="space-y-5 text-lg text-foreground/90 leading-relaxed max-w-2xl">
            {s.whatIsIt.map((p, i) => (
              <motion.p key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: i * 0.08, ease: EASE }}>{p}</motion.p>
            ))}
          </div>
        </div>
      </section>

      {/* Hvad får I? — asymmetric benefit grid */}
      <section className="relative z-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl mb-12">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Hvad får I?</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Konkrete gevinster fra dag ét</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
            {s.benefits.map((b, i) => {
              const span = i < 2 ? "lg:col-span-3" : "lg:col-span-2";
              return (
                <motion.div key={b.title} className={span}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.06, ease: EASE }}
                  data-testid={`benefit-${i}`}>
                  <div className="group h-full rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-foreground/25"
                    style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.03)" }}>
                    <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center mb-4 transition-colors duration-300 group-hover:bg-foreground">
                      <b.Icon className="h-5 w-5 text-foreground transition-colors duration-300 group-hover:text-background" strokeWidth={1.75} />
                    </div>
                    <h3 className="font-display text-lg font-medium">{b.title}</h3>
                    <p className="text-[13.5px] text-muted-foreground mt-2 leading-relaxed">{b.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sådan arbejder vi — fine-line process */}
      <section className="relative z-10 border-t border-border bg-secondary/50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl mb-14">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Sådan arbejder vi</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Fra idé til resultat i fire trin</h2>
          </div>
          {/* Desktop */}
          <div className="hidden md:block relative">
            <div className="absolute top-5 left-0 right-0 h-px bg-border" />
            <div className="relative grid grid-cols-4 gap-6">
              {s.process.map((step, i) => (
                <motion.div key={step.title} data-testid={`step-${i}`}
                  initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.45, delay: i * 0.08, ease: EASE }} className="flex flex-col items-start">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card relative z-10 text-[12px] font-medium" style={{ color: TEAL }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-lg font-medium mt-5">{step.title}</h3>
                  <p className="text-[13.5px] text-muted-foreground mt-2 leading-relaxed max-w-[15rem]">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
          {/* Mobile */}
          <div className="md:hidden relative pl-8">
            <div className="absolute left-[19px] top-2 bottom-2 w-px bg-border" />
            <div className="space-y-8">
              {s.process.map((step, i) => (
                <div key={step.title} className="relative flex items-start gap-4">
                  <span className="absolute -left-8 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-[12px] font-medium" style={{ color: TEAL }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="ml-4">
                    <h3 className="font-display text-lg font-medium">{step.title}</h3>
                    <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Hvornår giver det mening? */}
      <section className="relative z-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-[0.4fr_0.6fr] gap-8 lg:gap-16">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Hvornår giver det mening?</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Typisk et match for virksomheder, som…</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {s.whoFor.map((w, i) => (
              <motion.div key={w} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.35, delay: (i % 2) * 0.06, ease: EASE }}
                className="flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-4">
                <span className="flex h-5 w-5 items-center justify-center rounded-full shrink-0" style={{ backgroundColor: TEAL }}>
                  <Check className="h-3 w-3 text-white" strokeWidth={3} />
                </span>
                <span className="text-[15px] text-foreground">{w}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <BookCTA />
      <MarketingFooter />
    </div>
  );
}
